/**
 * useNpsSurveyAdapter — app-coupled props for the DS `<NpsModal>` / `<NpsSurvey>`.
 *
 * Owns NPS submit via redux + Amplitude. Eligibility lives in `useNpsFrequency`; pass
 * `markAsDismissed` in. No legacy frequency hooks / `useApiStatus`.
 */
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import * as amplitude from "@amplitude/analytics-react-native"

import { apiIdle, apiRequest } from "../../../../data/redux/actions/api"
import { npsService } from "../../../../data/services/npsService"
import { useGesture } from "./gestureTracking"

export type UseNpsSurveyAdapterConfig = {
    userEmail?: string | null
    triggerContext: string
    metadata?: Record<string, unknown>
    markAsDismissed: () => Promise<void> | void
}

type PendingKind = "score" | "comment"

type Pending = {
    kind: PendingKind
    score: number
    resolve: () => void
    reject: (error?: unknown) => void
}

type ApiSlice = {
    request?: boolean
    success?: boolean
    error?: { message?: string } | null
}

export function useNpsSurveyAdapter(config: UseNpsSurveyAdapterConfig) {
    const { userEmail = null, triggerContext, metadata, markAsDismissed } = config
    const dispatch = useDispatch()
    const pendingRef = useRef<Pending | null>(null)
    const handledSuccessRef = useRef(false)
    const handledErrorRef = useRef(false)

    const submitReq = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => state?.api?.submitScore as ApiSlice | undefined
    )
    const commentSubmitting = !!submitReq?.request
    const gesture = useGesture("nps.submit")

    const submitPayload = useCallback(
        (score: number, comment: string | null) => {
            handledSuccessRef.current = false
            handledErrorRef.current = false
            const payload: Record<string, unknown> = {
                score,
                comment,
                trigger_context: triggerContext,
                metadata,
            }
            if (userEmail) payload.email = userEmail
            dispatch(apiRequest(npsService.submitScore, {}, payload) as never)
            gesture.submitted()
        },
        [dispatch, metadata, triggerContext, userEmail, gesture]
    )

    useEffect(() => {
        if (!submitReq?.success || handledSuccessRef.current || !pendingRef.current) return
        handledSuccessRef.current = true
        gesture.succeeded()
        const pending = pendingRef.current
        pendingRef.current = null
        dispatch(apiIdle(npsService.submitScore) as never)
        if (pending.kind === "comment") {
            amplitude.track("nps_submitted", {
                score: pending.score,
                has_comment: true,
                trigger_context: triggerContext,
            })
        }
        pending.resolve()
    }, [dispatch, submitReq?.success, triggerContext, gesture])

    useEffect(() => {
        if (!submitReq?.error || handledErrorRef.current || !pendingRef.current) return
        handledErrorRef.current = true
        gesture.failed(submitReq.error)
        const pending = pendingRef.current
        pendingRef.current = null
        const error = submitReq.error
        dispatch(apiIdle(npsService.submitScore) as never)
        amplitude.track("nps_submission_failed", {
            trigger_context: triggerContext,
            error: error?.message,
            had_comment: pending.kind === "comment",
        })
        pending.reject(error)
    }, [dispatch, submitReq?.error, triggerContext, gesture])

    const onSelectScore = useCallback(
        (score: number) =>
            new Promise<void>((resolve, reject) => {
                pendingRef.current = { kind: "score", score, resolve, reject }
                submitPayload(score, null)
                amplitude.track("nps_score_submitted", {
                    score,
                    trigger_context: triggerContext,
                })
            }),
        [submitPayload, triggerContext]
    )

    const onSubmitComment = useCallback(
        (score: number, comment: string) =>
            new Promise<void>((resolve, reject) => {
                pendingRef.current = { kind: "comment", score, resolve, reject }
                submitPayload(score, comment)
                amplitude.track("nps_comment_submitted", {
                    score,
                    has_comment: !!comment.trim(),
                    trigger_context: triggerContext,
                })
            }),
        [submitPayload, triggerContext]
    )

    const onDismissWithoutScore = useCallback(async () => {
        amplitude.track("nps_dismissed", {
            trigger_context: triggerContext,
            had_score_selected: false,
        })
        await markAsDismissed()
    }, [markAsDismissed, triggerContext])

    const onSkipComment = useCallback(
        (score: number) => {
            amplitude.track("nps_comment_skipped", {
                score,
                trigger_context: triggerContext,
            })
        },
        [triggerContext]
    )

    return {
        modalProps: {
            onSelectScore,
            onSubmitComment,
            onDismissWithoutScore,
            onSkipComment,
            commentSubmitting,
        },
    }
}
